/* ==========================================================================
   MKTheme — storefront filtering & sorting (collection + search)
   Progressive enhancement: the filter form works as a normal GET form.
   With JS, changes re-render the section via the Section Rendering API.
   ========================================================================== */
(() => {
  if (customElements.get('facets-form')) return;

  const debounce = (fn, wait) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  class FacetsForm extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.debouncedRender = debounce(() => this.render(this.buildQuery()), 450);

      this.addEventListener('change', (e) => {
        const target = e.target;
        if (target.matches('[data-mobile-sort]')) {
          const desktop = this.querySelector('select[name="sort_by"]');
          if (desktop) desktop.value = target.value;
        } else if (target.matches('select[name="sort_by"]')) {
          const mobile = this.querySelector('[data-mobile-sort]');
          if (mobile) mobile.value = target.value;
        }
        if (target.type === 'number') return; // handled on input (debounced)
        if (target.form?.matches('.facets__form') || target.name === 'sort_by' || target.matches('[data-mobile-sort]')) this.render(this.buildQuery());
      });

      this.addEventListener('input', (e) => {
        if (e.target.type === 'number') this.debouncedRender();
      });

      this.addEventListener('submit', (e) => {
        if (!e.target.matches('form.facets__form')) return; // e.g. product card quick add
        e.preventDefault();
        this.render(this.buildQuery());
      });

      this.addEventListener('click', (e) => {
        const link = e.target.closest('a[data-facet-link]');
        if (link) {
          e.preventDefault();
          this.render(new URL(link.href, window.location.origin).searchParams.toString());
          return;
        }
        if (e.target.closest('[data-facets-open]')) this.toggle(true);
        else if (e.target.closest('[data-facets-close]')) this.toggle(false);
      });

      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) this.toggle(false);
      });

      if (!FacetsForm.popstateBound) {
        FacetsForm.popstateBound = true;
        window.addEventListener('popstate', () => {
          const el = document.querySelector('facets-form');
          if (el) el.render(window.location.search.slice(1), false);
        });
      }
    }

    toggle(open) {
      this.classList.toggle('is-open', open);
      document.body.classList.toggle('scroll-lock', open);
      if (open) this.querySelector('.facets__sidebar')?.focus?.();
    }

    buildQuery() {
      const form = this.querySelector('form.facets__form');
      const params = new URLSearchParams();
      if (form) {
        for (const [key, value] of new FormData(form)) {
          if (value !== '') params.append(key, value);
        }
      }
      const sort = this.querySelector('select[name="sort_by"]');
      if (sort && !params.has('sort_by')) params.set('sort_by', sort.value);
      // Keep the search query when there is no filter form (search without filters)
      const current = new URLSearchParams(window.location.search);
      if (current.has('q') && !params.has('q')) {
        params.set('q', current.get('q'));
        if (current.has('type')) params.set('type', current.get('type'));
        if (current.has('options[prefix]')) params.set('options[prefix]', current.get('options[prefix]'));
      }
      return params.toString();
    }

    async render(query, push = true) {
      const wasOpen = this.classList.contains('is-open');
      const openFacets = [...this.querySelectorAll('details[data-facet-id]')].map((d) => [d.dataset.facetId, d.open]);
      const activeId = document.activeElement && this.contains(document.activeElement) ? document.activeElement.id : null;

      this.classList.add('is-loading');
      try {
        const url = `${window.location.pathname}?${query}${query ? '&' : ''}section_id=${this.sectionId}`;
        const response = await fetch(url);
        const html = await response.text();
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('facets-form');
        if (!fresh) {
          window.location.search = query;
          return;
        }
        this.innerHTML = fresh.innerHTML;

        openFacets.forEach(([id, open]) => {
          const d = this.querySelector(`details[data-facet-id="${id}"]`);
          if (d) d.open = open;
        });
        if (wasOpen) this.classList.add('is-open');
        if (activeId) document.getElementById(activeId)?.focus({ preventScroll: true });

        if (push) {
          const next = `${window.location.pathname}${query ? '?' + query : ''}`;
          window.history.pushState({ facets: true }, '', next);
        }
        window.MKTheme?.initReveal?.(this);
        document.dispatchEvent(new CustomEvent('facets:updated', { detail: { query }, bubbles: true }));
      } catch (error) {
        window.location.search = query;
      } finally {
        this.classList.remove('is-loading');
      }
    }
  }

  customElements.define('facets-form', FacetsForm);
})();
